/* eslint-disable import/no-named-default */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import CustomButton from '../../../common/CustomButton/CustomButton';
import CustomLink from '../../../common/CustomLink/CustomLink';
import { Icon } from '../../../common/Icon/Icon';
import Image from '../../../common/Image/Image';
import Markdown from '../../../common/Markdown/Markdown';
import NoTagRenderer from '../../../common/NoTagRenderer/NoTagRenderer';
import { useScreenSizeContext } from '../../../core/context/screenSize.context';
import { slugify } from '../../../core/helpers';
import useFeedback from '../../../core/hooks/useFeedback';
import useModal from '../../../core/hooks/useModal';
import { IImage, ILink } from '../../../core/models';
import { default as AnthropicChat, IAnthropicAI } from '../../AnthropicAI/Chat/Chat';
import { IMindstudio } from '../../Mindstudio/Mindstudio';
import EmbeddedAI from '../../Mindstudio/QuestionCoach';
import Chat, { IOpenAI } from '../../OpenAI/Chat/Chat';
import './AICarouselItem.css';

export interface IAICarouselItem {
  title?: string;
  desc?: string;
  img?: IImage;
  mindstudio?: IMindstudio;
  openai?: IOpenAI;
  anthropicai?: IAnthropicAI;
  highestTitle: number | null;
  hidden?: boolean;
  cta?: ILink;
}

const AICarouselItem = forwardRef<HTMLHeadingElement, Partial<IAICarouselItem>>(
  (
    {
      title,
      desc,
      img,
      mindstudio,
      openai,
      anthropicai,
      highestTitle,
      cta,
    }: Partial<IAICarouselItem>,
    ref,
  ) => {
    const { width } = useScreenSizeContext();
    const btnRef = useRef<HTMLButtonElement>(null);
    const descRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const {
      closeModal,
      startTimer,
      feedbackCookie,
      Modal: FeedbackModal,
    } = useFeedback({ ai: anthropicai || openai, btnRef });
    const closeCallback = useMemo(
      () => ({
        closeCallback: closeModal,
      }),
      [closeModal],
    );
    const { modal, open, close } = useModal(closeCallback);

    const openModal = useCallback(() => {
      if (!btnRef.current) return;
      open(btnRef.current);
      if (!feedbackCookie) startTimer();
    }, [feedbackCookie, open, startTimer]);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => titleRef.current!, []);

    useEffect(() => {
      if (!descRef.current || !titleRef.current) return;
      const titleHeight = titleRef.current.scrollHeight;
      const heightDif = (highestTitle || 0) - titleHeight;
      descRef.current.style.marginTop = `${heightDif}px`;
    }, [highestTitle, width]);

    return (
      <div className="flex gap-sm">
        <div className="ai-carousel__slide-img-container">
          {!img ? null : (
            <Image className="ai-carousel__slide-img cover-img" img={img} size="small" />
          )}
        </div>
        <div className="ai-carousel__slide-content flex gap-sm">
          {!title ? null : (
            <h3 className="h3" ref={titleRef}>
              <Markdown
                components={{
                  p: NoTagRenderer,
                }}
              >
                {title}
              </Markdown>
            </h3>
          )}
          {!desc ? null : (
            <div ref={descRef}>
              <Markdown className="markdown">{desc}</Markdown>
            </div>
          )}
          <div className="ai-carousel__slide-actions">
            {!openai && !mindstudio && !anthropicai ? null : (
              <CustomButton
                className="btn btn--secondary"
                onClick={openModal}
                type="button"
                ref={btnRef}
                id={openai?.btnId || mindstudio?.btnId || anthropicai?.btnId}
              >
                {openai?.btnLabel || mindstudio?.btnLabel || anthropicai?.btnLabel || 'Open'}
              </CustomButton>
            )}
            {!cta ? null : <CustomLink link={cta} />}
          </div>
        </div>
        {!openai
          ? null
          : modal({
              side: 'right',
              opaque: true,
              keepMounted: true,
              children: (
                <>
                  <CustomButton
                    className="modal__close"
                    onClick={close}
                    type="button"
                    id={`${slugify(title || '')}-modal-close`}
                  >
                    <Icon.Plus className="icon-large" />
                    <span className="sr-only">Close modal</span>
                  </CustomButton>
                  {!openai?.assistantID ? null : <Chat ai={openai} />}
                </>
              ),
            })}
        {!mindstudio
          ? null
          : modal({
              opaque: true,
              children: (
                <>
                  <CustomButton
                    className="modal__close"
                    onClick={close}
                    type="button"
                    id={`${title ? slugify(title) : 'ai-item'}-modal-close`}
                  >
                    <Icon.Plus className="icon-large" />
                    <span className="sr-only">Close modal</span>
                  </CustomButton>
                  {!mindstudio?.appId || !mindstudio.publicToken ? null : (
                    <div className="ai-carousel__chat-container">
                      <EmbeddedAI publicToken={mindstudio.publicToken} appId={mindstudio.appId} />
                    </div>
                  )}
                </>
              ),
            })}
        {!anthropicai
          ? null
          : modal({
              side: 'right',
              opaque: true,
              keepMounted: true,
              children: (
                <>
                  <CustomButton
                    className="modal__close"
                    onClick={close}
                    type="button"
                    id={`${slugify(anthropicai?.title || '')}-modal-close`}
                  >
                    <Icon.Plus className="icon-large" />
                    <span className="sr-only">Close modal</span>
                  </CustomButton>
                  {!anthropicai ? null : <AnthropicChat ai={anthropicai} />}
                </>
              ),
            })}
        <FeedbackModal />
      </div>
    );
  },
);

export default AICarouselItem;
